import { StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import type { DraftSet } from './state/loggerDraft';
import type { useTheme } from './theme';
import type { DropsetPatch } from './dropsetDraft';
import { DropsetBlock } from './DropsetBlock';
import { HeaderTooltip } from './HeaderTooltip';
import { IconButton } from '../primitives/IconButton';
import { Chip } from '../primitives/Chip';

// FocusPanelDetails (timeline logger) — the dropset STEPS + read-only notes
// under the FocusPanel input. The Dropset|Myo-rep SELECTOR bar itself now lives
// in FocusPanel, directly under the qualifier row that hosts the 🔥 Burn+ chip
// (Antenore 2026-07-02: the selector should appear right below the row, not past
// the inputs). This component keeps only the per-step DropsetBlock (which
// belongs with the working-set inputs) + the read-only cue/note displays. It
// writes the SAME DraftSet.dropset data model — NO schema/save-path change.
//
// NOTE AUTHORING RETIRED IN THE LOGGER (Solution C, 2026-06-04): the per-entry
// 📝 note editor moved OUT of here to the single timeline NoteComposer (one
// comment space → timeline note events). An EXISTING note still renders
// read-only below (keep-render).
//
// ⭐ PE-37 — and it came BACK, for prescribe only, through `onEditEntryNote`.
// A program editor has no timeline to compose into: the coach is authoring the
// note the athlete will read later, so the pen belongs where the note is
// SHOWN. ⛔ Not in the card's action cluster: it was built there first and both
// the visual bench and the a11y audit measured it out — a fourth (fifth, when
// grouped) 44pt target does not fit beside the superset and week cues.
//
// AMRAP is intentionally absent: it's set via the reps field, not a menu entry
// ([[feedback_amrap_via_reps_form]]).
export function FocusPanelDetails({
  set,
  prescriptionNote,
  entryNote,
  isBodyweight,
  onChangeSet,
  onEditEntryNote,
  editLocked,
  theme,
}: {
  /** The bound (pending) set, or null when every set is logged. */
  set: DraftSet | null;
  /** Coach's per-set cue 🎯 for the bound set, already resolved with
   *  carry-forward (effectivePrescriptionNote) so a set-0-only cue shows on
   *  every set. Null when there's nothing to show. */
  prescriptionNote: string | null;
  /** The per-exercise note (💬) to render, already resolved by the parent:
   *  the coach's cue while a session is RUNNING, else the slot's own note
   *  when the card is authoring one (PE-37). Empty string when there's
   *  nothing to show. ⚠️ The old wording named `entry.notes`, a field the
   *  timeline logger had already retired. */
  entryNote: string;
  isBodyweight: boolean;
  /** PE-37 — PRESCRIBE ONLY (absent in the logger, where authoring stays
   *  retired). Opens the exercise-note editor. Present ⇒ the note row grows a
   *  pen, and an exercise with NO note still shows a way in — otherwise the
   *  affordance would exist only once a note already existed. */
  onEditEntryNote?: () => void;
  /** Writes a dropset patch onto the bound set (reps/weight/dropset only). */
  onChangeSet: (patch: DropsetPatch) => void;
  /** Editing a past session (edit-scope §9): adding steps to an existing dropset
   *  chain is locked (addLocked); existing step VALUES + the note stay editable. */
  editLocked: boolean;
  theme: ReturnType<typeof useTheme>;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);

  const hasNote = entryNote.trim().length > 0;

  return (
    <View style={styles.wrap}>
      {set?.dropset ? (
        <DropsetBlock
          dropset={set.dropset}
          isBodyweight={isBodyweight}
          inputsLocked={false}
          addLocked={editLocked}
          onChange={(next) => onChangeSet({ dropset: next })}
          theme={theme}
        />
      ) : null}

      {/* Coach's per-set prescription cue (read-only, 🎯). Hydrated onto the
          bound set by planStarterHydration; the card logger rendered this on
          every SetRow — removing the card logger (v3.33.0) deleted the only
          render site, so per-set cues silently stopped showing in the timeline
          logger even though the data is intact (Antenore 2026-06-18). Same
          read-only HeaderTooltip treatment as the exercise note below.
          Carry-forward (2026-06-19): a set-0-only cue (the common case — an
          exercise-level note authored on the first set) now shows on every set
          via effectivePrescriptionNote in the parent. */}
      {prescriptionNote ? (
        <View style={styles.noteDisplay}>
          <HeaderTooltip
            label={prescriptionNote}
            leadingIcon="target"
            tip={prescriptionNote}
            maxChars={500}
            labelNumberOfLines={2}
            alwaysOn
            labelStyle={styles.noteDisplayText}
            cellStyle={styles.noteCell}
            a11yLabel={t('mobile.logger.prescriptionReadA11y', {
              defaultValue: 'Prescribed set cue — tap to read in full',
            })}
            theme={theme}
          />
        </View>
      ) : null}

      {/* Read-only keep-render of an EXISTING entry note (authoring retired —
          new notes go through the timeline NoteComposer). Truncated to 2
          lines; a tap shows the FULL text for a few seconds via the
          transient tooltip — content, not a hint, so it bypasses the
          Hints gate (Antenore 2026-06-12). */}
      {hasNote ? (
        <View style={styles.noteDisplay}>
          <HeaderTooltip
            label={entryNote}
            leadingIcon="comment"
            tip={entryNote}
            maxChars={500}
            labelNumberOfLines={2}
            alwaysOn
            labelStyle={styles.noteDisplayText}
            // ⛔ `noteCellGrows`, not the bare cell: the row became a flex ROW
            // when the pen joined it, and without flex:1 + minWidth:0 a long
            // note refuses to shrink — the banco showed it running off the
            // edge and carrying the pen out of reach with it. A note that is
            // too long to fix is the note you most want to edit.
            cellStyle={styles.noteCellGrows}
            a11yLabel={t('mobile.logger.noteReadA11y', {
              defaultValue: 'Exercise note — tap to read in full',
            })}
            theme={theme}
          />
          {/* PE-37 — the pen sits BESIDE the text, not on it: the row's own tap
              already means "read it in full", and overloading one gesture with
              two jobs is how a tooltip becomes an accidental editor. */}
          {onEditEntryNote ? (
            <IconButton
              name="edit"
              onPress={onEditEntryNote}
              accessibilityLabel={t('mobile.logger.entryNoteEdit', {
                defaultValue: 'Edit the exercise note',
              })}
              hitSlop={8}
              theme={theme}
            />
          ) : null}
        </View>
      ) : onEditEntryNote ? (
        /* ⭐ No note yet, and this is the half that a "make the existing row
           editable" fix forgets: without a door here the coach could only ever
           edit a note that somehow already existed. A chip, not a full row —
           it is an invitation, not content. */
        <Chip
          theme={theme}
          dense
          selected={false}
          // Left-hugging, not stretched: the parent lays out in a column, so
          // without this the chip spans the card and reads as a primary
          // button. It is an invitation.
          style={styles.noteAddChip}
          label={t('mobile.logger.entryNoteAdd', {
            defaultValue: '＋ Exercise note',
          })}
          onPress={onEditEntryNote}
          accessibilityLabel={t('mobile.logger.entryNoteAdd', {
            defaultValue: '＋ Exercise note',
          })}
        />
      ) : null}
    </View>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    wrap: { gap: s.s2 },
    noteAddChip: { alignSelf: 'flex-start' },
    // The tooltip cell inside the note row: it must be the part that gives,
    // so the pen beside it keeps its 44pt however long the note is.
    // ⛔ EXTENDS noteCell, never replaces it: dropping its `alignItems:
    // 'flex-start'` let a SHORT note centre itself in the grown cell while a
    // long one stayed left — two notes in the same column starting at
    // different x. The banco caught it; the styles did not.
    noteCellGrows: {
      alignItems: 'flex-start',
      alignSelf: 'stretch',
      flex: 1,
      minWidth: 0,
    },
    noteDisplay: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s2,
      borderRadius: r.r2,
      backgroundColor: p.surface2,
      paddingHorizontal: s.s3,
      paddingVertical: s.s2,
    },
    noteDisplayText: { color: p.ink2, fontSize: ty.sizes.sm },
    // Left-align the tooltip trigger (HeaderTooltip centres by default for
    // its column-header use).
    noteCell: { alignItems: 'flex-start', alignSelf: 'stretch' },
  });
}
